import React, { useState } from 'react';
import { Button, Checkbox, Empty, Pagination, Spin } from 'antd';
import { DownOutlined, UpOutlined } from '@ant-design/icons';

/**
 * Lit la valeur d'une cellule à partir d'un `dataIndex` antd (chaîne ou chemin imbriqué)
 * @param {object} record - Ligne de données
 * @param {string|string[]} dataIndex - Clé ou chemin de la valeur
 */
const getCellValue = (record, dataIndex) => {
  if (dataIndex === undefined || dataIndex === null) return undefined;
  const path = Array.isArray(dataIndex) ? dataIndex : [dataIndex];
  return path.reduce((acc, key) => acc?.[key], record);
};

/**
 * Clé d'une colonne : `key`, sinon `dataIndex` (chaîne ou chemin), sinon son index
 */
const getColumnKey = (col, index) => {
  if (col.key !== undefined && col.key !== null) return col.key;
  if (Array.isArray(col.dataIndex)) return col.dataIndex.join('.');
  return col.dataIndex ?? index;
};

/**
 * Alternative mobile à <Table> : affiche chaque ligne sous forme de carte
 * générée à partir des MÊMES colonnes que le tableau (title, dataIndex, render).
 *
 * @param {object[]} columns - Colonnes au format antd Table
 * @param {object[]} dataSource - Lignes à afficher
 * @param {string|function} rowKey - Clé unique d'une ligne (défaut : 'id')
 * @param {boolean} loading - Affiche un spinner pendant le chargement
 * @param {function} onCardClick - Callback (record) au clic sur une carte
 * @param {object} rowSelection - Sélection au format antd ({ selectedRowKeys, onChange, getCheckboxProps })
 * @param {object} pagination - Configuration de pagination antd ({ current, pageSize, total, onChange })
 * @param {string[]} actionKeys - Clés des colonnes affichées en pied de carte (défaut : ['actions'])
 * @param {string[]} hiddenKeys - Clés des colonnes à ne pas afficher dans la carte
 * @param {string[]} headerKeys - Clés des colonnes affichées en haut de carte, pleine largeur et sans libellé
 * @param {function} expandedRowRender - (record) => ReactNode : contenu dépliable sous la carte (équivalent de `expandable.expandedRowRender`)
 * @param {number} clientPageSize - Si défini, pagine localement la liste (équivalent de `pagination.pageSize` d'un Table non paginé côté serveur)
 * @param {ReactNode} footer - Contenu affiché sous la liste (ex: ligne de total)
 * @param {string} emptyText - Texte affiché quand il n'y a aucune ligne
 */
const ColumnsCardList = ({
  columns = [],
  dataSource = [],
  rowKey = 'id',
  loading = false,
  onCardClick,
  rowSelection,
  pagination,
  actionKeys = ['actions'],
  hiddenKeys = [],
  headerKeys = [],
  expandedRowRender = null,
  clientPageSize = null,
  footer = null,
  emptyText = 'Aucune donnée'
}) => {
  // Page courante pour la pagination locale (`clientPageSize`)
  const [localPage, setLocalPage] = useState(1);
  // Clé de la carte dépliée (une seule à la fois)
  const [expandedKey, setExpandedKey] = useState(null);
  const keyedColumns = columns.map((col, index) => ({ ...col, key: getColumnKey(col, index) }));
  const getKey = (record) => (typeof rowKey === 'function' ? rowKey(record) : record[rowKey]);
  const headerColumns = keyedColumns.filter((col) => headerKeys.includes(col.key) && !hiddenKeys.includes(col.key));
  const bodyColumns = keyedColumns.filter((col) => !actionKeys.includes(col.key) && !hiddenKeys.includes(col.key) && !headerKeys.includes(col.key));
  const footerColumns = keyedColumns.filter((col) => actionKeys.includes(col.key));

  // Coche/décoche une carte en conservant le format de sélection d'antd
  const toggleSelection = (key, checked) => {
    const current = rowSelection.selectedRowKeys || [];
    const next = checked ? [...current, key] : current.filter((k) => k !== key);
    rowSelection.onChange?.(next);
  };

  // Lignes affichées : tranche de la page courante en pagination locale, sinon toutes
  const lastPage = clientPageSize ? Math.max(1, Math.ceil(dataSource.length / clientPageSize)) : 1;
  const currentLocalPage = Math.min(localPage, lastPage);
  const visibleRows = clientPageSize
    ? dataSource.slice((currentLocalPage - 1) * clientPageSize, currentLocalPage * clientPageSize)
    : dataSource;

  return (
    <Spin spinning={loading}>
      {dataSource.length === 0 && !loading ? (
        <Empty description={emptyText} />
      ) : (
        <div className="space-y-3">
          {visibleRows.map((record) => {
            const key = getKey(record);
            const checkboxProps = rowSelection?.getCheckboxProps?.(record) || {};
            return (
              <div
                key={key}
                onClick={onCardClick ? () => onCardClick(record) : undefined}
                className={`space-y-2 rounded-lg border border-gray-200 bg-white p-3 ${onCardClick ? 'cursor-pointer' : ''}`}
              >
                {/* Case de sélection (sans déclencher le clic de la carte) */}
                {rowSelection && (
                  <div onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={(rowSelection.selectedRowKeys || []).includes(key)}
                      disabled={checkboxProps.disabled}
                      onChange={(e) => toggleSelection(key, e.target.checked)}
                    />
                  </div>
                )}

                {/* Colonnes d'en-tête : pleine largeur, sans libellé */}
                {headerColumns.map((col) => (
                  <div key={col.key} className="min-w-0 break-words">
                    {col.render
                      ? col.render(getCellValue(record, col.dataIndex), record)
                      : getCellValue(record, col.dataIndex)}
                  </div>
                ))}

                {bodyColumns.map((col) => (
                  <div key={col.key} className="flex items-start justify-between gap-3">
                    <span className="shrink-0 pt-0.5 text-xs text-gray-500">{col.title}</span>
                    <div className="min-w-0 flex-1 break-words text-right">
                      {col.render
                        ? col.render(getCellValue(record, col.dataIndex), record)
                        : getCellValue(record, col.dataIndex)}
                    </div>
                  </div>
                ))}

                {/* Contenu dépliable (ex: performances d'une campagne) */}
                {expandedRowRender && (
                  <div onClick={(e) => e.stopPropagation()}>
                    <Button
                      block
                      size="small"
                      icon={expandedKey === key ? <UpOutlined /> : <DownOutlined />}
                      onClick={() => setExpandedKey(expandedKey === key ? null : key)}
                    >
                      {expandedKey === key ? 'Masquer les détails' : 'Voir les détails'}
                    </Button>
                    {expandedKey === key && <div className="mt-2 overflow-x-auto">{expandedRowRender(record)}</div>}
                  </div>
                )}

                {/* Actions en pied de carte */}
                {footerColumns.length > 0 && (
                  <div className="flex justify-end gap-2 border-t border-gray-100 pt-2">
                    {footerColumns.map((col) => (
                      <React.Fragment key={col.key}>{col.render?.(null, record)}</React.Fragment>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {footer}

      {clientPageSize && dataSource.length > clientPageSize && (
        <Pagination
          simple
          className="mt-4 text-center"
          current={currentLocalPage}
          pageSize={clientPageSize}
          total={dataSource.length}
          showSizeChanger={false}
          onChange={setLocalPage}
        />
      )}

      {pagination && (
        <Pagination
          simple
          className="mt-4 text-center"
          current={pagination.current}
          pageSize={pagination.pageSize}
          total={pagination.total}
          showSizeChanger={false}
          onChange={pagination.onChange}
        />
      )}
    </Spin>
  );
};

export default ColumnsCardList;
